import { useState, useRef } from "react";
import { UploadCloud, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { webApi, errText } from "@/lib/webApi";
import { PageHead, primary, ghost } from "@/pages/web/WebApp";

const MAX = 50 * 1024 * 1024;

function Result({ r, onFiled }) {
  const [name, setName] = useState("");
  const [done, setDone] = useState(null);
  const file = async (body) => {
    try {
      const { data } = await webApi.post("/upload/confirm", { fids: r.fids, ...body });
      setDone(data.where);
      onFiled();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="rounded-lg border border-white/10 bg-[#0E121A] p-4" data-testid="upload-result">
      <p className="text-sm font-medium text-slate-100">{r.file}</p>
      {r.error ? <p className="mt-2 text-sm text-red-400">{r.error}</p> : <p className="mt-2 whitespace-pre-line text-sm text-slate-400">{r.reply}</p>}
      {done && <p className="mt-3 flex items-center gap-1 text-sm text-emerald-300" data-testid="upload-filed"><Check className="h-4 w-4" /> Filed in {done}</p>}
      {!done && r.fids?.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {r.library && <button onClick={() => file({ library: true })} className={primary} data-testid="upload-to-library">Save to Library</button>}
          {r.options.map((o, i) => (
            <button key={i} onClick={() => file(o.id ? { matter_id: o.id } : { new_name: o.name, keys: o.keys })} className={i === 0 && !r.library ? primary : ghost} data-testid={`upload-option-${i}`}>
              {o.name}{!o.id && " (new)"}
            </button>
          ))}
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Or type a matter name" data-testid="upload-new-matter-input"
            className="rounded-md border border-white/10 bg-[#07090D] px-3 py-2 text-sm" />
          <button disabled={!name.trim()} onClick={() => file({ new_name: name })} className={ghost} data-testid="upload-new-matter-save">File</button>
        </div>
      )}
    </div>
  );
}

export function UploadView() {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [results, setResults] = useState([]);
  const input = useRef();
  const run = async (files) => {
    setBusy(true);
    for (const f of files) {
      if (f.size > MAX) {
        setResults((r) => [{ file: f.name, error: "Files can be up to 50 MB." }, ...r]);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await webApi.post("/upload", fd);
        setResults((r) => [{ file: f.name, ...data }, ...r]);
      } catch (e) {
        setResults((r) => [{ file: f.name, error: errText(e, "Upload failed.") }, ...r]);
      }
    }
    setBusy(false);
  };
  return (
    <div data-testid="web-upload">
      <PageHead title="Upload" sub="PDF, Word or photos, up to 50 MB each. Each file is read and summarised, and counts as one document read." />
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); run([...e.dataTransfer.files]); }}
        onClick={() => input.current?.click()} data-testid="upload-dropzone"
        className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 text-center transition-colors duration-200 ${drag ? "border-amber-300 bg-amber-300/5" : "border-white/10 hover:border-white/20"}`}>
        {busy ? <Loader2 className="h-8 w-8 animate-spin text-amber-300" /> : <UploadCloud className="h-8 w-8 text-slate-500" />}
        <p className="mt-3 text-slate-300">{busy ? "Reading your files..." : "Drop files here or click to choose"}</p>
        <input ref={input} type="file" multiple hidden accept=".pdf,.docx,.jpg,.jpeg,.png,.webp" data-testid="upload-file-input" onChange={(e) => run([...e.target.files])} />
      </div>
      <div className="mt-6 space-y-3">{results.map((r, i) => <Result key={i} r={r} onFiled={() => {}} />)}</div>
    </div>
  );
}
